import { defineComponent, ref, getCurrentInstance, computed, watch, nextTick } from '@vue/composition-api';
import Vue from 'vue';
import __vue_component__ from '../SdsIcon/SdsIcon.vue.js';

/**
 * @deprecated Use SdsInputCurrency instead
 */
var script = defineComponent({
    props: {
        isDisabled: {
            type: Boolean,
            required: false,
            default: false,
        },
        isErrorState: {
            type: Boolean,
            required: false,
            default: false,
        },
        variant: {
            type: String,
            required: false,
            default: 'white',
        },
        size: {
            type: String,
            required: false,
            default: 'lg',
        },
        currency: {
            type: String,
            required: false,
            default: '',
        },
        value: {
            type: [Number, String],
            required: false,
            default: null,
        },
        name: {
            type: String,
            required: true,
        },
        label: {
            type: String,
            required: true,
        },
        shouldFillDecimals: {
            type: Boolean,
            required: false,
            default: false,
        },
        max: {
            type: Number,
            required: false,
            // Setting a default max value so value can't go into exponential mode, causing weird bugs
            default: 99999999999999,
        },
    },
    emits: ['click', 'input', 'valueChanged'],
    // eslint-disable-next-line sonarjs/cognitive-complexity
    setup: function (props, _a) {
        var _b, _c;
        var emit = _a.emit;
        var decimalSeparator = Vue.prototype.$twoDecimals(11.11).toString().indexOf('.') !== -1 ? '.' : ',';
        var wrongDecimalSeparator = decimalSeparator === '.' ? ',' : '.';
        var initialValue = (_c = (_b = props.value) === null || _b === void 0 ? void 0 : _b.toString()) !== null && _c !== void 0 ? _c : '0';
        var internalValue = ref(props.shouldFillDecimals
            ? fillDecimals(initialValue)
            : initialValue);
        var lastCursorPosition = ref(0);
        var inputElement = ref();
        var instance = getCurrentInstance();
        var internalValueAsNumber = computed(function () {
            var value = internalValue.value.replace(',', '.');
            return parseFloat(value);
        });
        watch(function () { return props.value; }, function (newValue) {
            var newValueString;
            if (typeof newValue === 'number') {
                newValueString = newValue.toString().replace('.', decimalSeparator);
            }
            else {
                newValueString = newValue !== null && newValue !== void 0 ? newValue : '';
            }
            if (props.shouldFillDecimals) {
                newValueString = fillDecimals(newValueString);
            }
            internalValue.value = newValueString;
        });
        function onClick(event) {
            readCursorPositionFromField();
            emit('click', event);
        }
        function onInput(e) {
            var _a;
            readCursorPositionFromField();
            var targetValue = e.target.value;
            targetValue = targetValue.replace(wrongDecimalSeparator, decimalSeparator);
            if (Number(targetValue) > props.max) {
                internalValue.value = String(props.max);
            }
            else {
                internalValue.value = trimTo2Decimals(targetValue);
            }
            (_a = instance === null || instance === void 0 ? void 0 : instance.proxy) === null || _a === void 0 ? void 0 : _a.$forceUpdate();
            if (props.shouldFillDecimals) {
                internalValue.value = fillDecimals(internalValue.value);
                setCursorPosition();
            }
            if (!targetValue) {
                emit('input', null);
                emit('valueChanged', null, props.name);
                return;
            }
            emit('input', internalValueAsNumber.value);
            emit('valueChanged', internalValueAsNumber.value, props.name);
        }
        function onKeyPress(e) {
            var charCode = e.which ? e.which : e.keyCode;
            var comma = 44;
            var dot = 46;
            var alreadyContainsDecimalSeparator = internalValue.value.toString().includes(decimalSeparator);
            if ([comma, dot].includes(charCode)
                && alreadyContainsDecimalSeparator
                && !props.shouldFillDecimals) {
                // If we have decimals enabled, we allow duplicate separators as that logic will take care of them
                // This allows to naturally write decimals and go over decimal separator by pressing the key
                e.preventDefault();
            }
            if (charCode > 31 && (charCode < 48 || charCode > 57) && ![comma, dot].includes(charCode)) {
                e.preventDefault();
            }
        }
        function handleDecimalEdgeCases(e) {
            if (!props.shouldFillDecimals) {
                return;
            }
            // Disallow leading 0 so when it gets formatted we don't have to manually compensate
            // for the missing 0'es in lastCursorPosition
            if (e.data === '0' && lastCursorPosition.value === 0) {
                e.preventDefault();
                e.stopImmediatePropagation();
            }
            if (e.inputType === 'deleteContentForward' || e.inputType === 'deleteContentBackward') {
                var lookAheadOffset = e.inputType === 'deleteContentForward' ? 0 : -1;
                // Prevent default and only move the cursor when user is trying to delete the comma separator in any way
                // This prevents decimal bleeding into the normal number when removing the decimal separator
                if (internalValue.value.charAt(lastCursorPosition.value + lookAheadOffset)
                    === decimalSeparator) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    setCursorPosition(e.inputType === 'deleteContentForward' ? 1 : -1);
                }
            }
        }
        function readCursorPositionFromField() {
            lastCursorPosition.value = inputElement.value.selectionEnd;
        }
        function trimTo2Decimals(value) {
            if (!value) {
                return value;
            }
            var tokens = value.split(decimalSeparator);
            if (tokens[1] && tokens[1].length > 2) {
                return value.slice(0, -(tokens[1].length - 2));
            }
            return value;
        }
        function fillDecimals(value) {
            var numberParts = (value).split(decimalSeparator);
            var decimals = numberParts[1] ? "".concat(numberParts[1].concat('00').slice(0, 2)) : '00';
            return "".concat(numberParts[0]).concat(decimalSeparator).concat(decimals);
        }
        function setCursorPosition(offset) {
            if (offset === void 0) { offset = 0; }
            // Wait for input to update with any pending mutations
            nextTick(function () {
                var _a;
                // We also update our last cursor position with the new offset
                lastCursorPosition.value = lastCursorPosition.value + offset;
                (_a = inputElement.value) === null || _a === void 0 ? void 0 : _a.setSelectionRange(lastCursorPosition.value, lastCursorPosition.value);
            });
        }
        return {
            internalValue: internalValue,
            inputElement: inputElement,
            handleDecimalEdgeCases: handleDecimalEdgeCases,
            onInput: onInput,
            readCursorPositionFromField: readCursorPositionFromField,
            onClick: onClick,
            onKeyPress: onKeyPress,
        };
    },
    components: {
        SdsIcon: __vue_component__,
    },
});

export default script;
